import {type ControlledAutocompleteSelectOption} from '@hm/fundamental'
import {useQuery, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {QueryKeyName, getLocationsStats} from '../../common/backend'
import {HierarchyLevel, LocationStats} from '../../types'
import {LocationDtoType} from '../../types/backend.types'

// Global mode is handled by the page itself (calls with 'Area'), but mapping
// Global → Area here provides a safe fallback since global aggregates all areas.
const HIERARCHY_LEVEL_TO_LOCATION_TYPE: Record<HierarchyLevel, LocationDtoType> = {
  Global: LocationDtoType.Area,
  Area: LocationDtoType.Area,
  Region: LocationDtoType.Region,
  Country: LocationDtoType.Country,
  Plant: LocationDtoType.Plant
}

const collectOptions = (
  stats: LocationStats[],
  targetType: string
): ControlledAutocompleteSelectOption[] => {
  const result: ControlledAutocompleteSelectOption[] = []

  const traverse = (node: LocationStats) => {
    if (node.type === targetType) {
      result.push({value: node.id, label: node.name})
    }
    for (const sub of node.subLocations) {
      traverse(sub)
    }
  }

  for (const node of stats) {
    traverse(node)
  }

  return result
}

export const useHierarchyNodeOptions = (
  hierarchyLevel: HierarchyLevel
): UseQueryResult<ControlledAutocompleteSelectOption[], AxiosError> => {
  return useQuery({
    queryKey: [QueryKeyName.GetLocationsStats],
    queryFn: () => getLocationsStats(),
    staleTime: Infinity,
    select: (locationStats: LocationStats[]) => {
      const targetType = HIERARCHY_LEVEL_TO_LOCATION_TYPE[hierarchyLevel]
      return collectOptions(locationStats, targetType).toSorted((a, b) =>
        a.label.localeCompare(b.label)
      )
    }
  })
}
